import type { KeystrokeLog, LogOp } from './types'

export interface StripBucket {
  /** Bucket start in real ms. */
  readonly start: number
  readonly typedChars: number
  /** Characters removed, measured in UTF-16 units of the replaced range. */
  readonly deletedChars: number
  readonly stampChars: number
  readonly ops: number
}

export interface TypingStats {
  readonly totalOps: number
  readonly insertedChars: number
  readonly deletedChars: number
  readonly stampChars: number
  readonly assistedChars: number
  readonly medianDt: number
  /** deletedChars / insertedChars, 0 when nothing was inserted. */
  readonly correctionRatio: number
  readonly realMs: number
}

const EMPTY_BUCKET = { typedChars: 0, deletedChars: 0, stampChars: 0, ops: 0 }

const charCount = (text: string): number => Array.from(text).length
const isTyped = (op: LogOp): boolean => op.kind !== 'stamp' && op.kind !== 'assisted'

function addToBucket(bucket: StripBucket, op: LogOp): StripBucket {
  const inserted = charCount(op.text)
  return {
    start: bucket.start,
    typedChars: bucket.typedChars + (isTyped(op) ? inserted : 0),
    deletedChars: bucket.deletedChars + (op.to - op.from),
    stampChars: bucket.stampChars + (op.kind === 'stamp' ? inserted : 0),
    ops: bucket.ops + 1,
  }
}

/** Activity per `bucketMs` of real typing time. Buckets are contiguous from 0 to the last op. */
export function typingStrip(log: KeystrokeLog, bucketMs = 5000): readonly StripBucket[] {
  if (!(bucketMs > 0)) throw new RangeError(`bucketMs must be positive, got ${bucketMs}`)
  if (log.ops.length === 0) return []
  const buckets: StripBucket[] = []
  let elapsed = 0
  for (const op of log.ops) {
    elapsed += op.dt
    const index = Math.floor(elapsed / bucketMs)
    while (buckets.length <= index)
      buckets.push({ start: buckets.length * bucketMs, ...EMPTY_BUCKET })
    buckets[index] = addToBucket(buckets[index] as StripBucket, op)
  }
  return buckets
}

function median(values: readonly number[]): number {
  if (values.length === 0) return 0
  const sorted = [...values].sort((a, b) => a - b)
  const mid = sorted.length >>> 1
  return sorted.length % 2 === 1
    ? (sorted[mid] as number)
    : ((sorted[mid - 1] as number) + (sorted[mid] as number)) / 2
}

export function typingStats(log: KeystrokeLog): TypingStats {
  const sum = (pick: (op: LogOp) => number) => log.ops.reduce((acc, op) => acc + pick(op), 0)
  const insertedChars = sum((op) => charCount(op.text))
  const deletedChars = sum((op) => op.to - op.from)
  return {
    totalOps: log.ops.length,
    insertedChars,
    deletedChars,
    stampChars: sum((op) => (op.kind === 'stamp' ? charCount(op.text) : 0)),
    assistedChars: sum((op) => (op.kind === 'assisted' ? charCount(op.text) : 0)),
    medianDt: median(log.ops.map((op) => op.dt)),
    correctionRatio: insertedChars === 0 ? 0 : deletedChars / insertedChars,
    realMs: sum((op) => op.dt),
  }
}
